import { SuretyAccountId } from "@inrev/common";
import { useFetchAccountCompanies } from "@portal/api/account";
import { useCreateBondRequest, useFetchBondRequestOutcome } from "@portal/api/bond-request/agent";
import { type ReactNode, useMemo } from "react";
import {
	type BondRequestSubmissionAdapter,
	BondRequestSubmissionAdapterProvider,
} from "./BondRequestSubmissionAdapterContext";

type AgentBondRequestSubmissionAdapterProviderProps = {
	accountId: SuretyAccountId;
	children: ReactNode;
};

export const AgentBondRequestSubmissionAdapterProvider = ({
	accountId,
	children,
}: AgentBondRequestSubmissionAdapterProviderProps) => {
	const { createBondRequest } = useCreateBondRequest();
	const { companies, companiesIsLoading } = useFetchAccountCompanies(accountId);

	const adapter = useMemo<BondRequestSubmissionAdapter>(
		() => ({
			submitBondRequest: (payload, { accountId, onSuccess, onError }) => {
				if (!accountId) {
					onError();
					return;
				}

				createBondRequest({ accountId, data: payload }).then(
					(data) => onSuccess(data.id),
					() => onError(),
				);
			},
			useOutcomeQuery: (quoteId) => useFetchBondRequestOutcome(accountId, quoteId),
			companies,
			companiesIsLoading,
		}),
		[createBondRequest, accountId, companies, companiesIsLoading],
	);

	return (
		<BondRequestSubmissionAdapterProvider value={adapter}>
			{children}
		</BondRequestSubmissionAdapterProvider>
	);
};
